/* eslint-disable */
//
// "How to fix this" info popover. Renders a small ⓘ button next to a short alt/error message;
// clicking it opens a popover listing ordered recovery steps with a Copy button (for pasting into
// a ticket). Click-to-open (not hover), dismiss on outside-click/Esc, one open at a time, focus
// moves into the popover on open and returns to the trigger on close.
//
// Usage:
//   <HowToFixPopover steps={r.recoverySteps} />
// Renders nothing when `steps` is empty — callers can pass through unconditionally.
//
import React, { useEffect, useRef, useState } from "react";
import { Overlay, Popover, Button } from "react-bootstrap";

let popoverSeq = 0;

// Recovery steps arrive as either an array of strings OR a single newline-delimited string
// (the backend stores/serializes RecoverySteps as one text field — "one step per line").
// Normalize both to a trimmed, non-empty array here so callers can pass whatever they have.
export const toRecoverySteps = (steps) => {
  if (Array.isArray(steps)) return steps.map((s) => String(s || "").trim()).filter(Boolean);
  if (typeof steps === "string")
    return steps.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  return [];
};

/** True when there is at least one recovery step (array or newline string). */
export const hasRecoverySteps = (steps) => toRecoverySteps(steps).length > 0;

export default function HowToFixPopover({
  steps,
  title = "How to fix this",
  ariaLabel = "How to fix this",
}) {
  const list = toRecoverySteps(steps);
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  const targetRef = useRef(null);
  const bodyRef = useRef(null);
  const idRef = useRef(`howtofix-${++popoverSeq}`);

  // Esc closes; focus moves into the popover on open, back to the trigger on close.
  useEffect(() => {
    if (!show) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
      } else if (e.key === "Tab") {
        // Minimal focus trap — keep Tab within the popover's focusable controls.
        const focusables = bodyRef.current?.querySelectorAll("button");
        if (!focusables || focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    // Focus the first control inside the popover once it renders.
    const t = setTimeout(() => {
      const firstBtn = bodyRef.current?.querySelector("button");
      if (firstBtn) firstBtn.focus();
    }, 0);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      clearTimeout(t);
    };
  }, [show]);

  const close = () => {
    setShow(false);
    // Return focus to the trigger for keyboard users.
    if (targetRef.current) targetRef.current.focus();
  };

  const copySteps = () => {
    const text = list.map((s, i) => `${i + 1}. ${s}`).join("\n");
    const done = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(done);
    } else {
      done();
    }
  };

  if (list.length === 0) return null;

  return (
    <>
      <button
        type="button"
        ref={targetRef}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={show}
        onClick={(e) => {
          e.stopPropagation();
          setShow((s) => !s);
        }}
        className="btn btn-link p-0 ml-1 align-baseline"
        style={{ lineHeight: 1, verticalAlign: "middle" }}
        title={title}
      >
        <i className="fas fa-info-circle text-primary" />
      </button>

      <Overlay
        target={targetRef.current}
        show={show}
        placement="auto"
        rootClose
        rootCloseEvent="click"
        onHide={() => setShow(false)}
      >
        {(overlayProps) => (
          <Popover id={idRef.current} {...overlayProps} style={{ ...overlayProps.style, maxWidth: 340 }}>
            <Popover.Title as="h3" style={{ fontSize: "0.95rem" }}>
              {title}
            </Popover.Title>
            <Popover.Content>
              <div ref={bodyRef} role="dialog" aria-label={title}>
                <ol className="pl-4 mb-2" style={{ fontSize: "0.9rem" }}>
                  {list.map((s, i) => (
                    <li key={i} className="mb-1">
                      {s}
                    </li>
                  ))}
                </ol>
                <div className="d-flex justify-content-end" style={{ gap: "0.5rem" }}>
                  <Button variant="outline-secondary" size="sm" onClick={copySteps}>
                    <i className="far fa-copy mr-1" />
                    {copied ? "Copied" : "Copy"}
                  </Button>
                  <Button variant="secondary" size="sm" onClick={close}>
                    Close
                  </Button>
                </div>
              </div>
            </Popover.Content>
          </Popover>
        )}
      </Overlay>
    </>
  );
}
